import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent } from '@/components/ui/card';
import { AuditType, AuditPriority } from '@/types/asset-audit';
import type { AuditFormData } from '@/pages/audits/CreateAudit';
import { 
  ClipboardCheck, 
  FileSearch, 
  Shield, 
  TrendingUp, 
  AlertCircle,
  AlertTriangle,
  AlertOctagon,
  Zap
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface AuditDetailsStepProps {
  formData: AuditFormData;
  updateFormData: (updates: Partial<AuditFormData>) => void;
}

const AUDIT_TYPES = [
  {
    value: AuditType.Physical,
    label: 'Physical Audit',
    description: 'Verify physical presence and condition of assets',
    icon: ClipboardCheck,
  },
  {
    value: AuditType.Compliance,
    label: 'Compliance Audit',
    description: 'Check regulatory and policy compliance',
    icon: Shield,
  },
  {
    value: AuditType.Financial,
    label: 'Financial Audit',
    description: 'Verify asset values and depreciation',
    icon: TrendingUp,
  },
  {
    value: AuditType.Condition,
    label: 'Condition Assessment',
    description: 'Assess asset condition and maintenance needs',
    icon: FileSearch,
  },
];

const PRIORITY_LEVELS = [
  {
    value: AuditPriority.Low,
    label: 'Low',
    description: 'Routine audit, flexible timeline',
    icon: AlertCircle,
    color: 'text-blue-500',
  },
  {
    value: AuditPriority.Medium,
    label: 'Medium',
    description: 'Standard priority, normal timeline',
    icon: AlertTriangle,
    color: 'text-yellow-500',
  },
  {
    value: AuditPriority.High,
    label: 'High',
    description: 'Important audit, tight timeline',
    icon: AlertOctagon,
    color: 'text-orange-500',
  },
  {
    value: AuditPriority.Critical,
    label: 'Critical',
    description: 'Urgent audit, immediate attention',
    icon: Zap,
    color: 'text-red-500',
  },
];

export function AuditDetailsStep({ formData, updateFormData }: Readonly<AuditDetailsStepProps>) {
  return (
    <Card className="bg-card border rounded-lg shadow-sm">
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Audit Details</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Provide basic information about the audit you want to create.
            </p>
          </div>

      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title">
          Audit Title <span className="text-red-500">*</span>
        </Label>
        <Input
          id="title"
          placeholder="e.g., Q1 2024 IT Equipment Audit"
          value={formData.title}
          onChange={(e) => updateFormData({ title: e.target.value })}
          required
        />
        <p className="text-xs text-muted-foreground">
          Give your audit a clear, descriptive name
        </p>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Describe the purpose and scope of this audit..."
          value={formData.description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateFormData({ description: e.target.value })}
          rows={4}
          className="bg-white"
        />
        <p className="text-xs text-muted-foreground">
          Optional: Add details about what this audit will cover
        </p>
      </div>

      {/* Audit Type */}
      <div className="space-y-3">
        <Label>
          Audit Type <span className="text-red-500">*</span>
        </Label>
        <RadioGroup
          value={formData.type}
          onValueChange={(value: string) => updateFormData({ type: value as AuditType })}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {AUDIT_TYPES.map((type) => {
              const Icon = type.icon;
              const isSelected = formData.type === type.value;
              return (
                <label
                  key={type.value}
                  htmlFor={`audit-type-${type.value}`}
                  className={cn(
                    'flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  <RadioGroupItem value={type.value} id={`audit-type-${type.value}`} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={cn('h-4 w-4', isSelected && 'text-primary')} />
                      <span className="font-medium">{type.label}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{type.description}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </RadioGroup>
      </div>

      {/* Priority */}
      <div className="space-y-3">
        <Label>
          Priority Level <span className="text-red-500">*</span>
        </Label>
        <RadioGroup
          value={formData.priority}
          onValueChange={(value: string) => updateFormData({ priority: value as AuditPriority })}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {PRIORITY_LEVELS.map((priority) => {
              const Icon = priority.icon;
              const isSelected = formData.priority === priority.value;
              return (
                <label
                  key={priority.value}
                  htmlFor={`priority-${priority.value}`}
                  className={cn(
                    'flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  <RadioGroupItem value={priority.value} id={`priority-${priority.value}`} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={cn('h-4 w-4', priority.color)} />
                      <span className="font-medium">{priority.label}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{priority.description}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </RadioGroup>
      </div>
        </div>
      </CardContent>
    </Card>
  );
}
